import Phaser from 'phaser';
import { BaseEnemy, ALL_DIRS, Direction, GridQuery } from './BaseEnemy';
import { TILE } from '../../types';

type BossState = 'idle' | 'charge' | 'vulnerable' | 'stunned';

export type BossAttackKind = 'flame' | 'spread' | 'cross' | 'star' | 'burst';

export interface BossSpec {
  textureKey: string;
  hp: number;
  speedFactor: number;
  chargeMs: number;
  idleMs: number;
  vulnerableMs: number;
  displayName: string;
  attackKind: BossAttackKind;
  attackIntervalMs: number;
  attackRange: number;
}

export const BOSS_SPECS: Record<number, BossSpec> = {
  1:  { textureKey: 'boss-w1',  hp: 3,  speedFactor: 0.55, chargeMs: 1800, idleMs: 700, vulnerableMs: 2800, displayName: 'Țestoasa Bombardier',   attackKind: 'flame',  attackIntervalMs: 2800, attackRange: 3 },
  2:  { textureKey: 'boss-w2',  hp: 5,  speedFactor: 0.60, chargeMs: 1900, idleMs: 700, vulnerableMs: 2600, displayName: 'Liliacul Exploziv',       attackKind: 'flame',  attackIntervalMs: 2400, attackRange: 4 },
  3:  { textureKey: 'boss-w3',  hp: 6,  speedFactor: 0.65, chargeMs: 1900, idleMs: 700, vulnerableMs: 2400, displayName: 'Păianjenul cu 8 Bombe',   attackKind: 'spread', attackIntervalMs: 2200, attackRange: 4 },
  4:  { textureKey: 'boss-w4',  hp: 7,  speedFactor: 0.65, chargeMs: 2000, idleMs: 700, vulnerableMs: 2200, displayName: 'Dragonul Mic',             attackKind: 'spread', attackIntervalMs: 2000, attackRange: 5 },
  5:  { textureKey: 'boss-w5',  hp: 8,  speedFactor: 0.70, chargeMs: 2000, idleMs: 700, vulnerableMs: 2000, displayName: 'Norul Furtunos',           attackKind: 'cross',  attackIntervalMs: 2000, attackRange: 4 },
  6:  { textureKey: 'boss-w6',  hp: 8,  speedFactor: 0.70, chargeMs: 2000, idleMs: 700, vulnerableMs: 2000, displayName: 'Crocodilul Tox',           attackKind: 'cross',  attackIntervalMs: 1800, attackRange: 5 },
  7:  { textureKey: 'boss-w7',  hp: 9,  speedFactor: 0.75, chargeMs: 2100, idleMs: 700, vulnerableMs: 1800, displayName: 'Robotul Sfărâmat',         attackKind: 'star',   attackIntervalMs: 1800, attackRange: 5 },
  8:  { textureKey: 'boss-w8',  hp: 10, speedFactor: 0.75, chargeMs: 2100, idleMs: 700, vulnerableMs: 1800, displayName: 'Yeti-ul Vânător',          attackKind: 'star',   attackIntervalMs: 1600, attackRange: 5 },
  9:  { textureKey: 'boss-w9',  hp: 11, speedFactor: 0.80, chargeMs: 2200, idleMs: 700, vulnerableMs: 1600, displayName: 'Phoenixul Bombelor',       attackKind: 'burst',  attackIntervalMs: 1500, attackRange: 6 },
  10: { textureKey: 'boss-w10', hp: 14, speedFactor: 0.80, chargeMs: 2200, idleMs: 700, vulnerableMs: 1500, displayName: 'PIEROS — Regele Bombelor', attackKind: 'burst',  attackIntervalMs: 1200, attackRange: 6 },
};

export class Boss extends BaseEnemy {
  readonly spec: BossSpec;
  hp: number;
  private bossState: BossState = 'idle';
  private stateUntil = 0;
  private nextAttackAt = 0;
  private getPlayerPositions: () => Array<{ x: number; y: number; alive: boolean }>;
  private onAttack: (col: number, row: number, kind: BossAttackKind, dx: number, dy: number) => void;
  private halo!: Phaser.GameObjects.Graphics;
  private haloTween!: Phaser.Tweens.Tween;

  constructor(
    scene: Phaser.Scene,
    spec: BossSpec,
    col: number,
    row: number,
    grid: GridQuery,
    getPlayerPositions: () => Array<{ x: number; y: number; alive: boolean }>,
    onAttack: (col: number, row: number, kind: BossAttackKind, dx: number, dy: number) => void,
  ) {
    super(scene, spec.textureKey, col, row, spec.speedFactor, grid);
    this.spec = spec;
    this.hp = spec.hp;
    this.getPlayerPositions = getPlayerPositions;
    this.onAttack = onAttack;
    this.baseScale = this.baseScale * 1.8;
    this.setScale(this.baseScale);

    this.halo = scene.add.graphics();
    this.halo.setDepth(-1);
    this.drawHalo(0xffe066, 0.45);
    this.haloTween = scene.tweens.add({
      targets: this.halo,
      alpha: { from: 0.4, to: 0.9 },
      yoyo: true,
      repeat: -1,
      duration: 700,
      ease: 'Sine.easeInOut',
    });
  }

  private drawHalo(color: number, alpha: number) {
    this.halo.clear();
    const r = TILE * 1.1;
    this.halo.fillStyle(color, alpha);
    this.halo.fillCircle(0, 0, r);
    this.halo.lineStyle(3, color, 0.95);
    this.halo.strokeCircle(0, 0, r);
  }

  update(time: number, delta: number) {
    if (!this.alive) return;
    this.animateWalk(delta);
    if (this.halo) this.halo.setPosition(this.x, this.y);

    if (time >= this.stateUntil) this.transition(time);

    if (this.bossState === 'charge' && this.isCenteredOnTile(6)) {
      const dir = this.chooseDirTowardPlayer();
      if (dir) this.applyDir(dir);
      else (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    }
    if (this.bossState === 'idle' || this.bossState === 'vulnerable' || this.bossState === 'stunned') {
      (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    }

    // Boss shoots attacks periodically (not while stunned)
    if (this.bossState !== 'stunned' && time >= this.nextAttackAt) {
      this.nextAttackAt = time + this.spec.attackIntervalMs;
      this.fireAttack();
    }
  }

  private fireAttack() {
    const col = this.gridCol();
    const row = this.gridRow();
    const players = this.getPlayerPositions().filter(p => p.alive);
    // Primary direction: toward nearest player; default down if no players
    let dx = 0, dy = 1;
    if (players.length > 0) {
      const me = { x: this.x, y: this.y };
      const target = players.reduce((best, p) =>
        Phaser.Math.Distance.BetweenPoints(me, p) < Phaser.Math.Distance.BetweenPoints(me, best) ? p : best
      , players[0]);
      const adx = Math.abs(target.x - this.x);
      const ady = Math.abs(target.y - this.y);
      if (adx > ady) { dx = target.x > this.x ? 1 : -1; dy = 0; }
      else            { dx = 0; dy = target.y > this.y ? 1 : -1; }
    }
    this.onAttack(col, row, this.spec.attackKind, dx, dy);
  }

  private transition(time: number) {
    switch (this.bossState) {
      case 'idle':
        this.bossState = 'charge';
        this.stateUntil = time + this.spec.chargeMs;
        this.clearTint();
        this.drawHalo(0xffe066, 0.4);
        break;
      case 'charge':
        this.bossState = 'vulnerable';
        this.stateUntil = time + this.spec.vulnerableMs;
        this.setTint(0xff6b6b);
        this.drawHalo(0xef476f, 0.55);
        (this.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
        break;
      case 'vulnerable':
        this.bossState = 'idle';
        this.stateUntil = time + this.spec.idleMs;
        this.clearTint();
        this.drawHalo(0xffe066, 0.4);
        break;
      case 'stunned':
        this.bossState = 'idle';
        this.stateUntil = time + this.spec.idleMs;
        this.clearTint();
        this.drawHalo(0xffe066, 0.4);
        break;
    }
  }

  private chooseDirTowardPlayer(): Direction | null {
    const players = this.getPlayerPositions().filter(p => p.alive);
    if (players.length === 0) {
      const open = ALL_DIRS.filter(d => this.canMoveTo(d));
      return open.length ? Phaser.Utils.Array.GetRandom(open) as Direction : null;
    }
    const me = { x: this.x, y: this.y };
    const target = players.reduce((best, p) => {
      const d = Phaser.Math.Distance.BetweenPoints(me, p);
      const bd = Phaser.Math.Distance.BetweenPoints(me, best);
      return d < bd ? p : best;
    }, players[0]);
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const prefer: Direction[] = Math.abs(dx) > Math.abs(dy)
      ? [dx > 0 ? 'right' : 'left', dy > 0 ? 'down' : 'up']
      : [dy > 0 ? 'down' : 'up', dx > 0 ? 'right' : 'left'];
    for (const d of prefer) if (this.canMoveTo(d)) return d;
    const open = ALL_DIRS.filter(d => this.canMoveTo(d));
    return open.length ? Phaser.Utils.Array.GetRandom(open) as Direction : null;
  }

  tryHit(): boolean {
    if (!this.alive) return false;
    if (this.bossState === 'charge' || this.bossState === 'stunned') return false;
    this.hp -= 1;
    this.scene.tweens.add({ targets: this, alpha: { from: 1, to: 0.3 }, yoyo: true, duration: 80, repeat: 3 });
    this.bossState = 'stunned';
    this.stateUntil = this.scene.time.now + 500;
    this.drawHalo(0xffffff, 0.7);
    if (this.hp <= 0) this.kill();
    return true;
  }

  kill() {
    this.haloTween?.stop();
    this.halo?.destroy();
    super.kill();
  }
}
